// PurchasesSection — unified view of a user's purchases.
// Tabs: Services (partner service bookings) + Products (shop orders).
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const SERVICE_STATUS = {
  pending:           { label: 'Pending',          tone: 'bg-amber-100 text-amber-700',   icon: 'ph-clock'        },
  paid:              { label: 'Confirmed',        tone: 'bg-emerald-100 text-emerald-700', icon: 'ph-check-circle' },
  enquiry_received:  { label: 'Enquiry Received', tone: 'bg-blue-100 text-blue-700',     icon: 'ph-chat-circle'  },
  completed:         { label: 'Completed',        tone: 'bg-emerald-100 text-emerald-700', icon: 'ph-check-circle' },
  cancelled:         { label: 'Cancelled',        tone: 'bg-rose-100 text-rose-600',     icon: 'ph-x-circle'     },
  refunded:          { label: 'Refunded',         tone: 'bg-gray-100 text-gray-600',     icon: 'ph-arrow-u-up-left' },
};

const ORDER_STATUS = {
  pending:    { label: 'Pending',    tone: 'bg-amber-100 text-amber-700',    icon: 'ph-clock'        },
  paid:       { label: 'Paid',       tone: 'bg-emerald-100 text-emerald-700', icon: 'ph-check-circle' },
  confirmed:  { label: 'Confirmed',  tone: 'bg-emerald-100 text-emerald-700', icon: 'ph-check-circle' },
  shipped:    { label: 'Shipped',    tone: 'bg-blue-100 text-blue-700',      icon: 'ph-truck'        },
  delivered:  { label: 'Delivered',  tone: 'bg-emerald-100 text-emerald-700', icon: 'ph-package'      },
  cancelled:  { label: 'Cancelled',  tone: 'bg-rose-100 text-rose-600',      icon: 'ph-x-circle'     },
  refunded:   { label: 'Refunded',   tone: 'bg-gray-100 text-gray-600',      icon: 'ph-arrow-u-up-left' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
};

const fmtINR = (amt) => `₹${(Number(amt) || 0).toLocaleString('en-IN')}`;

function StatusPill({ status, map, testId }) {
  const meta = map[status] || { label: status || 'Unknown', tone: 'bg-gray-100 text-gray-600', icon: 'ph-info' };
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${meta.tone}`}
    >
      <i className={`ph-bold ${meta.icon} text-[11px]`} />
      {meta.label}
    </span>
  );
}

function ServiceBookingCard({ booking, idx }) {
  const variantLabel = booking.variant?.label
    || (booking.variant?.duration_minutes ? `${booking.variant.duration_minutes} min` : '');
  return (
    <div
      data-testid={`profile-purchase-service-${idx}`}
      className="bg-white rounded-2xl p-4 border border-gray-100 hover:border-[#3AAFA9]/40 transition-all"
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#3AAFA9]/15 to-[#2B3A67]/10 flex items-center justify-center shrink-0">
          <i className="ph-bold ph-sparkle text-[#2B3A67] text-lg" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-bold text-sm text-[#2B3A67] truncate" data-testid={`purchase-service-name-${idx}`}>
                {booking.service_name || 'Service'}
              </p>
              {booking.partner_name && (
                <p className="text-[11px] text-gray-500 truncate">by {booking.partner_name}</p>
              )}
            </div>
            <StatusPill
              status={booking.status}
              map={SERVICE_STATUS}
              testId={`purchase-service-status-${idx}`}
            />
          </div>

          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-600">
            {variantLabel && (
              <span className="inline-flex items-center gap-1">
                <i className="ph ph-clock-countdown" /> {variantLabel}
              </span>
            )}
            {booking.preferred_date && (
              <span className="inline-flex items-center gap-1">
                <i className="ph ph-calendar-blank" /> {booking.preferred_date}
                {booking.preferred_slot ? ` · ${booking.preferred_slot}` : ''}
              </span>
            )}
            {booking.city && (
              <span className="inline-flex items-center gap-1">
                <i className="ph ph-map-pin" /> {booking.city}
              </span>
            )}
          </div>

          <div className="mt-3 flex items-center justify-between">
            <p className="text-[10px] text-gray-400">
              Booked {fmtDate(booking.created_at)}
            </p>
            {!booking.is_combo_enquiry && (
              <p className="text-sm font-extrabold text-[#E54562]" data-testid={`purchase-service-amount-${idx}`}>
                {fmtINR(booking.total_amount)}
              </p>
            )}
            {booking.is_combo_enquiry && (
              <p className="text-[11px] font-bold text-blue-700">Enquiry only</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProductOrderCard({ order, idx, onView }) {
  const itemCount = (order.items || []).reduce((acc, it) => acc + (it.quantity || 1), 0);
  const firstItemName = order.items?.[0]?.name || 'Order';
  return (
    <div
      data-testid={`profile-purchase-order-${idx}`}
      className="bg-white rounded-2xl p-4 border border-gray-100 hover:border-[#E54562]/40 transition-all"
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#E54562]/15 to-[#F4D341]/20 flex items-center justify-center shrink-0">
          <i className="ph-bold ph-shopping-bag text-[#E54562] text-lg" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-bold text-sm text-[#2B3A67] truncate" data-testid={`purchase-order-title-${idx}`}>
                {firstItemName}
                {itemCount > 1 && <span className="text-gray-400 font-normal"> +{itemCount - 1} more</span>}
              </p>
              <p className="text-[11px] text-gray-500">Order #{order.id.slice(0, 6).toUpperCase()}</p>
            </div>
            <StatusPill
              status={order.status}
              map={ORDER_STATUS}
              testId={`purchase-order-status-${idx}`}
            />
          </div>

          <div className="mt-3 flex items-center justify-between">
            <p className="text-[10px] text-gray-400">
              Placed {fmtDate(order.created_at)}
            </p>
            <p className="text-sm font-extrabold text-[#E54562]" data-testid={`purchase-order-amount-${idx}`}>
              {fmtINR(order.total_amount)}
            </p>
          </div>

          {onView && (
            <button
              onClick={() => onView(order)}
              className="mt-3 text-[11px] font-bold text-[#2B3A67] hover:underline"
              data-testid={`purchase-order-view-${idx}`}
            >
              View order →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PurchasesSection({ serviceBookings = [], orders = [] }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('services');
  const totalCount = serviceBookings.length + orders.length;

  return (
    <section
      className="bg-white rounded-2xl p-5 shadow-md border border-gray-100"
      data-testid="profile-purchases"
    >
      <div className="flex items-center justify-between mb-1">
        <div>
          <h3 className="font-extrabold text-[#2B3A67]">My Purchases</h3>
          <p className="text-[11px] text-gray-500 mt-0.5">
            {totalCount === 0 ? 'Track your service bookings and shop orders here.' : `${totalCount} item${totalCount !== 1 ? 's' : ''} purchased`}
          </p>
        </div>
        <button
          onClick={() => navigate(tab === 'services' ? '/services' : '/shop')}
          className="text-[11px] font-bold text-[#E54562] hover:underline"
          data-testid="profile-purchases-explore"
        >
          {tab === 'services' ? 'Browse services' : 'Browse shop'} →
        </button>
      </div>

      {/* Tabs */}
      <div className="mt-4 inline-flex p-1 bg-gray-100 rounded-full text-xs font-bold">
        <button
          onClick={() => setTab('services')}
          className={`px-4 py-1.5 rounded-full transition ${
            tab === 'services' ? 'bg-white text-[#2B3A67] shadow' : 'text-gray-500'
          }`}
          data-testid="purchases-tab-services"
        >
          <i className="ph-bold ph-sparkle mr-1.5" />
          Services
          <span className="ml-1.5 text-[10px] text-gray-400">({serviceBookings.length})</span>
        </button>
        <button
          onClick={() => setTab('products')}
          className={`px-4 py-1.5 rounded-full transition ${
            tab === 'products' ? 'bg-white text-[#2B3A67] shadow' : 'text-gray-500'
          }`}
          data-testid="purchases-tab-products"
        >
          <i className="ph-bold ph-shopping-bag mr-1.5" />
          Shop
          <span className="ml-1.5 text-[10px] text-gray-400">({orders.length})</span>
        </button>
      </div>

      {/* Content */}
      <div className="mt-4 space-y-3">
        {tab === 'services' && (
          serviceBookings.length === 0 ? (
            <EmptyState
              testId="profile-purchases-services-empty"
              icon="ph-sparkle"
              title="No service bookings yet"
              cta="Explore wellness, beauty & lifestyle services"
              onCta={() => navigate('/services')}
            />
          ) : (
            serviceBookings.map((b, idx) => (
              <ServiceBookingCard key={b.id} booking={b} idx={idx} />
            ))
          )
        )}

        {tab === 'products' && (
          orders.length === 0 ? (
            <EmptyState
              testId="profile-purchases-orders-empty"
              icon="ph-shopping-bag"
              title="No shop orders yet"
              cta="Discover gear, jerseys & merchandise"
              onCta={() => navigate('/shop')}
            />
          ) : (
            orders.map((o, idx) => (
              <ProductOrderCard key={o.id} order={o} idx={idx} />
            ))
          )
        )}
      </div>
    </section>
  );
}

function EmptyState({ testId, icon, title, cta, onCta }) {
  return (
    <div
      className="text-center py-8 px-4 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200"
      data-testid={testId}
    >
      <div className="w-12 h-12 mx-auto rounded-full bg-white flex items-center justify-center shadow-sm">
        <i className={`ph-bold ${icon} text-[#E54562] text-xl`} />
      </div>
      <p className="mt-3 text-sm font-bold text-[#2B3A67]">{title}</p>
      <button
        onClick={onCta}
        className="mt-3 px-4 py-2 rounded-full bg-[#2B3A67] text-white text-xs font-bold hover:bg-[#1f2a4d] transition"
      >
        {cta}
      </button>
    </div>
  );
}
